import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Achievements.css';

const Achievements = ({ userId, achievements, stats, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [newUnlocks, setNewUnlocks] = useState([]);

  useEffect(() => {
    checkForNewAchievements();
  }, [userId]);

  const checkForNewAchievements = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/metrics/achievements/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.newUnlocks && response.data.newUnlocks.length > 0) {
        setNewUnlocks(response.data.newUnlocks);
        setTimeout(() => setNewUnlocks([]), 5000);
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Error checking achievements:', err);
    } finally {
      setLoading(false);
    }
  };

  const allAchievements = [
    {
      id: 'week-streak',
      name: 'Week Warrior',
      description: 'Maintain a 7-day streak',
      icon: '🔥',
      category: 'streak'
    },
    {
      id: 'month-streak',
      name: 'Monthly Master',
      description: 'Maintain a 30-day streak',
      icon: '💎',
      category: 'streak'
    },
    {
      id: 'quarter-streak',
      name: 'Quarter Champion',
      description: 'Maintain a 90-day streak',
      icon: '👑',
      category: 'streak'
    },
    {
      id: 'first-question',
      name: 'First Steps',
      description: 'Answer your first question',
      icon: '🎯',
      category: 'milestone'
    },
    {
      id: 'century',
      name: 'Century Club',
      description: 'Answer 100 questions',
      icon: '💯',
      category: 'milestone'
    },
    {
      id: 'millennium',
      name: 'Millennium Mind',
      description: 'Answer 1000 questions',
      icon: '🏆',
      category: 'milestone'
    },
    {
      id: 'perfect-ten',
      name: 'Perfect Ten',
      description: 'Get 10 questions correct in a row',
      icon: '⭐',
      category: 'accuracy'
    },
    {
      id: 'accuracy-master',
      name: 'Accuracy Master',
      description: '90% accuracy over 50 questions',
      icon: '🎯',
      category: 'accuracy'
    },
    {
      id: 'algebra-master',
      name: 'Algebra Master',
      description: 'Achieve mastery in Algebra',
      icon: '📐',
      category: 'mastery'
    },
    {
      id: 'geometry-master',
      name: 'Geometry Master',
      description: 'Achieve mastery in Geometry',
      icon: '📏',
      category: 'mastery'
    },
    {
      id: 'trig-master',
      name: 'Trigonometry Master',
      description: 'Achieve mastery in Trigonometry',
      icon: '📊',
      category: 'mastery'
    },
    {
      id: 'social-butterfly',
      name: 'Social Butterfly',
      description: 'Follow 10 users',
      icon: '🦋',
      category: 'social'
    },
    {
      id: 'helpful-peer',
      name: 'Helpful Peer',
      description: 'Comment on 20 posts',
      icon: '💬',
      category: 'social'
    },
    {
      id: 'night-owl',
      name: 'Night Owl',
      description: 'Study after midnight',
      icon: '🦉',
      category: 'special'
    },
    {
      id: 'early-bird',
      name: 'Early Bird',
      description: 'Study before 6 AM',
      icon: '🌅',
      category: 'special'
    },
    {
      id: 'weekend-warrior',
      name: 'Weekend Warrior',
      description: 'Study on weekends',
      icon: '⚔️',
      category: 'special'
    }
  ];

  const getAchievementStatus = (achievementId) => {
    return achievements.some(a => a.id === achievementId);
  };

  const groupedAchievements = allAchievements.reduce((acc, achievement) => {
    if (!acc[achievement.category]) {
      acc[achievement.category] = [];
    }
    acc[achievement.category].push({
      ...achievement,
      unlocked: getAchievementStatus(achievement.id),
      unlockedData: achievements.find(a => a.id === achievement.id)
    });
    return acc;
  }, {});

  const categoryNames = {
    streak: 'Streak Achievements',
    milestone: 'Milestone Achievements',
    accuracy: 'Accuracy Achievements',
    mastery: 'Subject Mastery',
    social: 'Social Achievements',
    special: 'Special Achievements'
  };

  return (
    <div className="achievements-container">
      {newUnlocks.length > 0 && (
        <div className="new-unlocks-banner">
          <h3>New Achievements Unlocked!</h3>
          <div className="new-unlocks-list">
            {newUnlocks.map(unlock => (
              <div key={unlock.id} className="new-unlock-item">
                <span className="unlock-icon">{unlock.icon}</span>
                <span className="unlock-name">{unlock.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="achievements-stats">
        <div className="stat-card">
          <div className="stat-value">{achievements.length}</div>
          <div className="stat-label">Unlocked</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{allAchievements.length - achievements.length}</div>
          <div className="stat-label">Locked</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {Math.round((achievements.length / allAchievements.length) * 100)}%
          </div>
          <div className="stat-label">Progress</div>
        </div>
      </div>

      <div className="achievements-grid">
        {Object.entries(groupedAchievements).map(([category, categoryAchievements]) => (
          <div key={category} className="achievement-category">
            <h3>{categoryNames[category]}</h3>
            <div className="achievements-list">
              {categoryAchievements.map(achievement => (
                <div 
                  key={achievement.id}
                  className={`achievement-item ${achievement.unlocked ? 'unlocked' : 'locked'}`}
                >
                  <div className="achievement-icon">{achievement.icon}</div>
                  <div className="achievement-details">
                    <h4>{achievement.name}</h4>
                    <p>{achievement.description}</p>
                    {achievement.unlocked && achievement.unlockedData && (
                      <span className="unlock-date">
                        Unlocked: {new Date(achievement.unlockedData.unlockedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {loading && (
        <div className="achievements-loading">
          <div className="spinner"></div>
          <p>Checking for new achievements...</p>
        </div>
      )}
    </div>
  );
};

export default Achievements;